import { Inject, Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth, UserClaims } from '@okta/okta-auth-js';
import { take } from 'rxjs/operators';
import { filterType } from 'src/util/rxjs/operators';
import { EmployeeHttpClient } from '../flight-log-api/employees/employee-client.service';
import {
  AuthProxyService,
  ImpersonateUserEvent,
  ImpersonateUserEventResponse,
  LoginEvent,
  LoginEventResponse,
  LogoutEvent,
  LogoutEventResponse,
  StopImpersonatingEvent,
  StopImpersonatingEventResponse,
} from './auth-proxy.service';

export const impersonatedUserIdKey = 'flightlog-impersonated-user-id';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private _initialized = false;

  constructor(
    private _employeeClient: EmployeeHttpClient,
    @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth,
    private _oktaState: OktaAuthStateService,
    private _snackBar: MatSnackBar,
    private _authProxy: AuthProxyService,
  ) {
    this._oktaState.authState$.subscribe((authState) => {
      this._updateAuthentication(authState.accessToken?.accessToken);
    });

    this._authProxy.events$.pipe(filterType(LoginEvent)).subscribe((msg) => {
      this._authProxy.sendEvent(
        new LoginEventResponse(msg.id, this._login(msg.payload?.redirectUrl)),
      );
    });

    this._authProxy.events$.pipe(filterType(LogoutEvent)).subscribe((msg) => {
      this._authProxy.sendEvent(new LogoutEventResponse(msg.id, this._logout(msg.payload ?? {})));
    });

    this._authProxy.events$.pipe(filterType(ImpersonateUserEvent)).subscribe((msg) => {
      this._authProxy.sendEvent(
        new ImpersonateUserEventResponse(msg.id, this._impersonateUser(msg.payload)),
      );
    });

    this._authProxy.events$.pipe(filterType(StopImpersonatingEvent)).subscribe((msg) => {
      this._authProxy.sendEvent(
        new StopImpersonatingEventResponse(msg.id, this._stopImpersonating()),
      );
    });
  }

  public init() {
    if (this._initialized) {
      throw new Error('AuthenticationService: init method called, but service already initialized');
    }
    this._initialized = true;
  }

  private async _updateAuthentication(token?: string) {
    this._authProxy.setAccessToken(token);

    const claims: UserClaims | undefined =
      token == null ? undefined : JSON.parse(atob(token.split('.')[1]));

    this._authProxy.setClaims(claims);

    if (token != null) {
      const impersonatedId = localStorage.getItem(impersonatedUserIdKey);
      if (impersonatedId != null) {
        await this._impersonateUser(impersonatedId);
      } else {
        await this._stopImpersonating();
      }
    }
  }

  private async _impersonateUser(userId: string) {
    const employee = await this._employeeClient.getById(userId).pipe(take(1)).toPromise();
    if (employee != null) {
      localStorage.setItem(impersonatedUserIdKey, userId);
      this._authProxy.setImpersonatedUserId(userId);
    }
  }

  private _stopImpersonating() {
    localStorage.removeItem(impersonatedUserIdKey);
    this._authProxy.setImpersonatedUserId(undefined);
  }

  private async _login(fromUri?: string): Promise<void> {
    return this._oktaAuth.signInWithRedirect({ originalUri: fromUri });
  }

  private async _logout(options: { alertText?: string; redirectUrl?: string }): Promise<void> {
    localStorage.clear();
    sessionStorage.clear();
    await this._oktaAuth.signOut({
      state: options?.redirectUrl ?? '/',
    });
    if (options.alertText != null) {
      this._snackBar.open(options.alertText, undefined, {
        duration: 2000,
      });
    }
  }
}
